import React, { useEffect } from 'react'
import styled from 'styled-components'

import i18n from '../../i18n'
import { ModalConsumer } from '../../context/ModalContext'
import SecureIcon from '../../assets/secure.svg'
import { Column as ColumnType } from '../../types'

import breakpoints from '../../constants/breakpoints'
import Container from '../../components/Layout/Container'
import JustifyCenter from '../../components/Layout/JustifyCenter'
import LinkButton from '../../components/LinkButton'
import Icon from '../../components/Icon'
import Readspeaker from '../../components/Readspeaker'

interface Props {
  title: string
  columns: ColumnType[]
  nextLink: string
  backButton?: string
}

const Introduction: React.FC<Props> = ({ title, columns, nextLink, backButton }) => {
  useEffect(() => {
    window.localStorage.clear()
  }, [])

  let smallScreen = window.innerHeight < 300

  window.addEventListener('resize', () => {
    return (smallScreen = window.innerHeight < 300)
  })

  return (
    <IntroductionContainer id="readspeakerArea" $smallScreen={smallScreen}>
      <Container>
        <Content>
          <Section>
            <Readspeaker />
            <JustifyCenter>
              <h1>{title}</h1>
            </JustifyCenter>

            <Blocks>
              {columns.map((introduction) => (
                <Block key={introduction.title}>
                  <SubTitle>
                    <h2>{introduction.title}</h2>
                  </SubTitle>
                  <p>{introduction.description}</p>
                </Block>
              ))}
            </Blocks>

            <StartTheApp>
              <LinkButton to={nextLink} secondary>
                {i18n.t('introduction.button')}
              </LinkButton>
              {backButton && (
                <LinkButton to={backButton} transparent>
                  {i18n.t('introduction.backButton')}
                </LinkButton>
              )}
            </StartTheApp>

            <Secure>
              <Icon icon={SecureIcon} />
              {i18n.t('introduction.policy')}
              <ModalConsumer>
                {({ openModal }) => (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      openModal(
                        i18n.t('introduction.moreInformation.title'),
                        i18n.t('introduction.moreInformation.content'),
                      )
                    }}
                  >
                    {i18n.t('introduction.moreInformation.button')}
                  </a>
                )}
              </ModalConsumer>
            </Secure>
          </Section>
        </Content>
      </Container>
    </IntroductionContainer>
  )
}

const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 40px;
  width: 100%;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    justify-content: space-between;
  }
`

const Block = styled.div`
  text-align: center;
  margin: 0 10px;
  flex: 1;
`

const SubTitle = styled.div`
  @media (min-width: ${breakpoints.md}) {
    height: 56px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
  }
`

const Section = styled.div`
  align-items: center;
  box-sizing: border-box;
  color: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 80px 0;

  & h1 {
    text-align: center;
    margin-bottom: 20px;

    @media (min-width: ${breakpoints.sm}) {
      margin-bottom: 80px;
    }
  }

  @media (min-width: ${breakpoints.sm}) {
    padding: 80px 0;
  }
`

const IntroductionContainer = styled.div<{ $smallScreen: boolean }>`
  background-color: ${({ theme }) => theme.blue};
  height: 100%;
  width: 100%;
  overflow-y: auto;
  position: absolute;

  @media (min-width: ${breakpoints.sm}) {
    height: calc(var(--vh) * 100);
  }

  ${({ $smallScreen }) =>
    $smallScreen &&
    `
    position: relative;
  `}
`

const Secure = styled.div`
  color: white;
  font-size: 14px;
  padding: 25px 0;
  text-align: center;
  width: 100%;

  & a {
    color: white;
    margin-left: 10px;
  }

  & div {
    display: inline-block;
  }

  & svg {
    margin-right: 10px;
    vertical-align: text-top;
    fill: white;
  }
`

const StartTheApp = styled.div`
  text-align: center;

  @media (min-width: ${breakpoints.sm}) {
    margin-top: 42px;
  }

  @media (min-width: ${breakpoints.sm}) {
    display: flex;
  }

  & a {
    display: inline-block;
  }

  & a,
  button {
    margin-bottom: 20px;
    min-width: 230px;

    &:last-child {
      margin-bottom: 0;
    }

    @media (min-width: ${breakpoints.sm}) {
      margin: 0 5px;
    }
  }
`

export default Introduction
